import { formatHex, parse, interpolate, converter } from "culori";

const DEFAULT_BORDER = "#EBEBEB";
const DEFAULT_TEXT_MUTED = "#5C5C5C";
const LUMINANCE_THRESHOLD = 0.5;
const BORDER_LIGHTEN_AMOUNT = 0.12;
const BORDER_DARKEN_AMOUNT = 0.08;
const MUTED_INTERPOLATION = 0.35;
const DARK_LIGHTNESS_OFFSET = 0.25;
const DARK_CHROMA_FACTOR = 0.55;
const LIGHT_LIGHTNESS_OFFSET = 0.25;
const LIGHT_CHROMA_FACTOR = 0.75;

const toOklch = converter("oklch");

const safeParse = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  const parsed = parse(hex.startsWith("#") ? hex : `#${hex}`);
  return parsed || null;
};

/**
 * Deriva a cor da borda do background:
 * - Background escuro (L < 0.5): borda levemente mais clara
 * - Background claro (L >= 0.5): borda levemente mais escura
 */
export const deriveBorderFromBackground = (backgroundHex) => {
  const bg = safeParse(backgroundHex);
  if (!bg) return DEFAULT_BORDER;

  const oklch = toOklch(bg);
  if (!oklch || oklch.l == null) return DEFAULT_BORDER;

  const l = oklch.l;
  const white = "#ffffff";
  const black = "#000000";

  try {
    let result;
    if (l < LUMINANCE_THRESHOLD) {
      result = interpolate([bg, white], "oklch")(BORDER_LIGHTEN_AMOUNT);
    } else {
      result = interpolate([bg, black], "oklch")(BORDER_DARKEN_AMOUNT);
    }
    return result ? formatHex(result) : DEFAULT_BORDER;
  } catch {
    return DEFAULT_BORDER;
  }
};

/**
 * Deriva texto muted a partir da cor do texto, interpolando em direção ao background
 */
export const deriveTextMuted = (textHex, backgroundHex) => {
  const text = safeParse(textHex);
  const bg = safeParse(backgroundHex);
  if (!text || !bg) return DEFAULT_TEXT_MUTED;

  try {
    const result = interpolate([text, bg], "oklch")(MUTED_INTERPOLATION);
    return result ? formatHex(result) : DEFAULT_TEXT_MUTED;
  } catch {
    return DEFAULT_TEXT_MUTED;
  }
};

/**
 * Versão mais escura da cor (para contraste com primary/secondary)
 */
export const deriveColorDark = (hex) => {
  const color = safeParse(hex);
  if (!color) return hex;

  const oklch = toOklch(color);
  if (!oklch || oklch.l == null) return hex;

  try {
    const newL = Math.max(0, oklch.l - DARK_LIGHTNESS_OFFSET);
    const newC = Math.max(0, (oklch.c ?? 0) * DARK_CHROMA_FACTOR);
    const result = { mode: "oklch", l: newL, c: newC, h: oklch.h ?? 0 };
    return formatHex(result) || hex;
  } catch {
    return hex;
  }
};

/**
 * Versão mais clara da cor
 */
export const deriveColorLight = (hex) => {
  const color = safeParse(hex);
  if (!color) return hex;

  const oklch = toOklch(color);
  if (!oklch || oklch.l == null) return hex;

  try {
    const newL = Math.min(1, oklch.l + LIGHT_LIGHTNESS_OFFSET);
    const newC = Math.min(1, (oklch.c ?? 0) * LIGHT_CHROMA_FACTOR);
    const result = { mode: "oklch", l: newL, c: newC, h: oklch.h ?? 0 };
    return formatHex(result) || hex;
  } catch {
    return hex;
  }
};
